import { doc, setDoc, deleteDoc, serverTimestamp, collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../firebase/config";

/** Firestore rechaza `undefined`; limpia objetos/arrays antes de setDoc/addDoc. */
export function stripUndefinedDeep(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    if (val === undefined) continue;
    const cleaned = stripUndefinedDeep(val);
    if (cleaned !== undefined) out[key] = cleaned;
  }
  return out;
}

export async function saveCmsDoc(collectionName, docId, data) {
  const uid = auth.currentUser?.uid || null;
  const cleaned = stripUndefinedDeep(data) || {};
  await setDoc(
    doc(db, collectionName, docId),
    {
      ...cleaned,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true }
  );
}

export async function createCmsDoc(collectionName, data) {
  const uid = auth.currentUser?.uid || null;
  const cleaned = stripUndefinedDeep(data) || {};
  const refDoc = await addDoc(collection(db, collectionName), {
    ...cleaned,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  });
  return refDoc.id;
}

export async function removeCmsDoc(collectionName, docId) {
  await deleteDoc(doc(db, collectionName, docId));
}

export async function uploadCmsImage(section, file) {
  if (!file) throw new Error("Archivo requerido");
  if (!file.type.startsWith("image/")) throw new Error("Solo imágenes");
  if (file.size > 5 * 1024 * 1024) throw new Error("Máximo 5 MB");

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `cms/${section}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
}

export function Field({ label, children }) {
  return (
    <label className="block space-y-2">
      <span className="text-[11px] font-bold uppercase tracking-widest text-brand-dark/55">
        {label}
      </span>
      {children}
    </label>
  );
}

export const inputClass =
  "w-full rounded-2xl border border-brand-soft bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-brand-primary text-sm";

export const textareaClass = `${inputClass} min-h-[110px] resize-y`;

export function SaveBar({ saving, onSave, message, error }) {
  return (
    <div className="sticky bottom-4 z-20 mt-8">
      <div className="bg-white/95 backdrop-blur border border-brand-soft rounded-2xl shadow-lg px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm">
          {error && <span className="text-red-600 font-medium">{error}</span>}
          {!error && message && <span className="text-brand-primary font-medium">{message}</span>}
          {!error && !message && (
            <span className="text-brand-dark/50">Los cambios se guardan en Firestore.</span>
          )}
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="bg-brand-primary text-white px-6 py-3 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-dark disabled:opacity-60"
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
